import {
  useQuery,
  useQueryClient,
  useMutation,
  useSuspenseQuery,
  useInfiniteQuery,
  queryOptions,
  keepPreviousData,
  type InfiniteData,
} from '@tanstack/react-query'
import {
  type EnumResponse,
  type AccommodationDetail,
  type LocationSummary,
  type UnitPhotos,
  type UnitAvailability,
  type LocationRoute,
  type LocationSearchResult,
  type SimilarOffer,
  type OrderSummaryRequest,
  type OrderSummaryResult,
  type Order,
  type OrderRequest,
  type CreateOrderResponse,
  type OrderPaymentUrl,
  type LocationInquiryRequest,
  type LocationInquiryResponse,
  type LocationPriceReportRequest,
  type LocationPriceReportResponse,
  type CatalogItem,
} from './types'
import accommodationApi from './accomodationApi'
import { HttpError } from '@/api/http'
import { type LocationSearchParams } from '@/features/accommodation/hooks/useLocatiomSearchParams'
import { parseCategorySlugs } from '@/features/accommodation/search/utils/categoryOptions'

// Resolve a region slug from the URL to the numeric ID the API expects.
// Returns undefined when slug is 'all'/empty (no filter) or when enums aren't loaded yet.
export function resolveRegionId(
  regionSlug: string | undefined,
  regions: { id: number; slug: string }[] | undefined,
): number | undefined {
  if (!regionSlug || regionSlug === 'all') return undefined
  return regions?.find((r) => r.slug === regionSlug)?.id
}

// Resolve comma-separated category slugs to the comma-separated IDs the API
// expects. Unknown slugs are dropped; returns undefined when none resolves
// (nothing selected, enums not loaded yet, or a stale slug in the URL).
export function resolveCategoryIds(
  categorySlugs: string | undefined,
  categories: { id: number; slug?: string }[] | undefined,
): string | undefined {
  const ids = parseCategorySlugs(categorySlugs)
    .map((slug) => categories?.find((c) => c.slug === slug)?.id)
    .filter((id) => id !== undefined)
  return ids.length > 0 ? ids.join(',') : undefined
}

// ============================================
// Query Options (for use with queryClient.query in route loaders)
// ============================================

export const accommodationDetailQueryOptions = (slug: string, unitSlug: string) =>
  queryOptions<AccommodationDetail>({
    queryKey: ['accommodation', slug, unitSlug],
    queryFn: ({ signal }) => accommodationApi.accommodationDetailFetch(slug, unitSlug, signal),
  })

export const locationSummaryQueryOptions = (slugOrId: string) =>
  queryOptions<LocationSummary>({
    queryKey: ['location', slugOrId],
    queryFn: ({ signal }) => accommodationApi.locationSummaryFetch(slugOrId, signal),
  })

export const offerDetailQueryOptions = (offerId: string) =>
  queryOptions<LocationSearchResult>({
    queryKey: ['offer', offerId],
    queryFn: ({ signal }) => accommodationApi.offerDetailFetch(offerId, signal),
  })

export const unitPhotosQueryOptions = (unitId: number) =>
  queryOptions<UnitPhotos>({
    queryKey: ['units', unitId, 'photos'],
    queryFn: ({ signal }) => accommodationApi.unitPhotosFetch(unitId, signal),
  })

export const unitAvailabilityQueryOptions = (unitId: number) =>
  queryOptions<UnitAvailability>({
    queryKey: ['units', unitId, 'availability'],
    queryFn: ({ signal }) => accommodationApi.unitAvailabilityFetch(unitId, signal),
  })

export const enumsQueryOptions = () =>
  queryOptions<EnumResponse>({
    queryKey: ['enums'],
    queryFn: ({ signal }) => accommodationApi.enumsFetch(signal),
  })

export const orderQueryOptions = (orderNumber: string) =>
  queryOptions<Order>({
    queryKey: ['order', orderNumber],
    queryFn: ({ signal }) => accommodationApi.getOrderFetch(orderNumber, signal),
  })

export const catalogQueryOptions = () =>
  queryOptions<CatalogItem[]>({
    queryKey: ['catalog'],
    queryFn: ({ signal }) => accommodationApi.catalogFetch(signal),
  })

// ============================================
// Query Hooks
// ============================================

export const locationSearchQueryOptions = (
  searchParams: LocationSearchParams,
  regionId: ReturnType<typeof resolveRegionId>,
  categoryIds: ReturnType<typeof resolveCategoryIds>,
) =>
  queryOptions({
    queryKey: ['location', searchParams, regionId, categoryIds],
    queryFn: ({ signal }) => accommodationApi.searchLocationsFetch(searchParams, signal, regionId, categoryIds),
  })

export const useLocationSearchQuery = (searchParams: LocationSearchParams) => {
  const { data: enums } = useQuery(enumsQueryOptions())
  const regionId = resolveRegionId(searchParams.region, enums?.regions)
  const categoryIds = resolveCategoryIds(searchParams.category, enums?.locationCategories)
  return useQuery(locationSearchQueryOptions(searchParams, regionId, categoryIds))
}

export const useInfiniteLocationSearchQuery = (searchParams: Omit<LocationSearchParams, 'skip'>, pageSize: number) => {
  const { data: enums } = useQuery(enumsQueryOptions())
  const regionId = resolveRegionId(searchParams.region, enums?.regions)
  const categoryIds = resolveCategoryIds(searchParams.category, enums?.locationCategories)
  return useInfiniteQuery<
    LocationSearchResult[],
    Error,
    InfiniteData<LocationSearchResult[], number>,
    (string | number | undefined | Omit<LocationSearchParams, 'skip'>)[],
    number
  >({
    queryKey: ['location', 'infinite', searchParams, pageSize, regionId, categoryIds],
    queryFn: ({ signal, pageParam }) =>
      accommodationApi.searchLocationsFetch(
        { ...searchParams, skip: pageParam, take: pageSize },
        signal,
        regionId,
        categoryIds,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < pageSize) {
        return undefined
      }
      return allPages.length * pageSize
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - prevents refetch on back navigation
  })
}

export const useSimilarOffersQuery = (searchParams: LocationSearchParams) => {
  const { data: enums } = useQuery(enumsQueryOptions())
  const regionId = resolveRegionId(searchParams.region, enums?.regions)
  const categoryIds = resolveCategoryIds(searchParams.category, enums?.locationCategories)
  return useQuery<SimilarOffer[]>({
    queryKey: ['offer', 'similar', searchParams, regionId, categoryIds],
    queryFn: ({ signal }) => accommodationApi.similarOffersFetch(searchParams, signal, regionId, categoryIds),
    // Without enums the region and type would be left out of the search.
    enabled: enums !== undefined,
    staleTime: 5 * 60 * 1000,
  })
}

export const useOfferDetailSuspenseQuery = (offerId: string) => useSuspenseQuery(offerDetailQueryOptions(offerId))

export const useCatalogSuspenseQuery = () => useSuspenseQuery(catalogQueryOptions())

export const useAccommodationDetailSuspenseQuery = (slug: string, unitSlug: string) =>
  useSuspenseQuery(accommodationDetailQueryOptions(slug, unitSlug))

export const useUnitAvailabilityQuery = (unitId: number) => useQuery(unitAvailabilityQueryOptions(unitId))

export const useUnitAvailabilitySuspenseQuery = (unitId: number) =>
  useSuspenseQuery(unitAvailabilityQueryOptions(unitId))

export const useEnumsQuery = () => useQuery(enumsQueryOptions())

export const useEnumsSuspenseQuery = () => useSuspenseQuery(enumsQueryOptions())

export const useOrderSummaryQuery = (order: OrderSummaryRequest | null, options?: { enabled?: boolean }) =>
  useQuery<OrderSummaryResult>({
    queryKey: ['order', 'summary', order],
    queryFn: async ({ signal }): Promise<OrderSummaryResult> => {
      try {
        const response = await accommodationApi.getOrderSummaryFetch(order!, signal)
        return {
          available: true,
          standardPrice: response.standardPrice,
          discountPrice: response.discountPrice,
          requestExpiresAt: response.requestExpiresAt ?? null,
        }
      } catch (error) {
        // API returns 400 when dates are unavailable (already booked, outside window, etc.)
        // We don't have a specific error payload to check, so we assume all 400s mean unavailable.
        // The error is already logged to Sentry in http.ts before being thrown, so we have visibility.
        if (error instanceof HttpError && error.status === 400) {
          return { available: false }
        }
        throw error
      }
    },
    staleTime: 0,
    gcTime: 0,
    // Keep the last known price while a new guest count / date range is being
    // priced, otherwise the price disappears and the layout jumps around it.
    placeholderData: keepPreviousData,
    enabled: order !== null && options?.enabled !== false,
    retry: false,
  })

export const useOrderSuspenseQuery = (orderNumber: string) => useSuspenseQuery(orderQueryOptions(orderNumber))

export const useCreateOrderMutation = () =>
  useMutation<CreateOrderResponse, Error, OrderRequest>({
    mutationFn: (order: OrderRequest) => accommodationApi.createOrderFetch(order, new AbortController().signal),
  })

export const useCancelOrderRequestMutation = (orderNumber: string, token: string) => {
  const queryClient = useQueryClient()

  return useMutation<Order, Error, void>({
    mutationFn: () => accommodationApi.cancelOrderRequestFetch(orderNumber, token, new AbortController().signal),
    // The response carries no scopes, the order page loads the full order again.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['order', orderNumber] }),
  })
}

export const locationRouteQueryOptions = (slug: string, coords: { latitude: number; longitude: number }) =>
  queryOptions<LocationRoute>({
    queryKey: ['location', slug, 'route', coords],
    queryFn: ({ signal }) => accommodationApi.locationRouteFetch(slug, coords.latitude, coords.longitude, signal),
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })

export const useLocationRouteQuery = (slug: string, coords: { latitude: number; longitude: number } | null) => {
  const hasValidCoords =
    coords !== null &&
    Number.isFinite(coords.latitude) &&
    Number.isFinite(coords.longitude) &&
    !(coords.latitude === 0 && coords.longitude === 0)
  return useQuery<LocationRoute>({
    ...locationRouteQueryOptions(slug, coords ?? { latitude: 0, longitude: 0 }),
    enabled: hasValidCoords,
  })
}

export const useCreateLocationInquiryMutation = (slug: string) =>
  useMutation<LocationInquiryResponse, Error, LocationInquiryRequest>({
    mutationFn: (inquiry: LocationInquiryRequest) => accommodationApi.createLocationInquiryFetch(slug, inquiry),
  })

export const useCreateLocationPriceReportMutation = (slug: string) =>
  useMutation<LocationPriceReportResponse, Error, LocationPriceReportRequest>({
    mutationFn: (report: LocationPriceReportRequest) => accommodationApi.createLocationPriceReportFetch(slug, report),
  })

// Every call creates a fresh Stripe Checkout Session that expires in 30
// minutes, and the API rejects orders that are no longer in the created state.
// Both make this a click-triggered mutation rather than a query — fetching it
// on render burned sessions and raced with the webhook on return from Stripe.
export const useOrderPaymentUrlMutation = (orderNumber: string) =>
  useMutation<OrderPaymentUrl, Error, string>({
    mutationFn: (returnUrl: string) =>
      accommodationApi.getOrderPaymentUrlFetch(orderNumber, returnUrl, new AbortController().signal),
  })

export type LocationDiscount = {
  minPrice: number
  maxPrice: number
}

const computeDiscount = (data: UnitAvailability | undefined): LocationDiscount | undefined => {
  if (!data || data.length === 0) return undefined

  const arrivalDates = data.filter((a) => a.state === 'arrival' && !a.closedToArrival)
  if (arrivalDates.length === 0) return undefined

  // Lowest available discounted price — the "from" (od) price we advertise.
  const minDiscountPrice = Math.min(...arrivalDates.map((a) => a.discountPrice))

  // Average standard (pre-discount) price, used as the strikethrough reference.
  const avgStandardPrice = arrivalDates.reduce((sum, a) => sum + a.standardPrice, 0) / arrivalDates.length

  return {
    minPrice: minDiscountPrice,
    maxPrice: avgStandardPrice,
  }
}

export const useUnitDiscountQuery = (unitId: number) => {
  const { data, isFetched } = useUnitAvailabilityQuery(unitId)

  return {
    discount: computeDiscount(data),
    isFetched,
  }
}

export const useUnitDiscountSuspenseQuery = (unitId: number) => {
  const { data } = useUnitAvailabilitySuspenseQuery(unitId)

  return {
    discount: computeDiscount(data),
  }
}
